import { useNavigate } from 'react-router-dom';
import { ChevronRight, FilePen, Loader2 } from 'lucide-react';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { queryLoadFailure } from '../../lib/load-failure';
import { formatInstant } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useMyDrafts } from '../../hooks/useMyDrafts';

/**
 * "My drafts" on the field Forms tab: the caller's own saved, unsubmitted
 * custom-form submissions. Tapping one reopens it prefilled
 * (`/forms/dynamic/:templateId?draft=:id`), and it can be submitted from
 * there.
 *
 * The read is `useMyDrafts`, shared with the field Safety tile.
 *
 * Renders nothing when there are no drafts. The section is an extra on a
 * forms list, and "No drafts" above every library would be noise. A failed
 * read still says so, because a missing draft list must not look like having
 * no drafts.
 */
export default function MyDraftsList() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const query = useMyDrafts();

  const failure = queryLoadFailure(query);
  if (failure) {
    return (
      <section className="mt-5" aria-label="My drafts">
        <h2 className="text-sm font-semibold text-white mb-2">My drafts</h2>
        <LoadFailureNotice error={query.error ?? query.failureReason} kind={failure} subject="your drafts" />
      </section>
    );
  }
  if (query.isLoading) {
    return (
      <div className="mt-5 flex justify-center py-2">
        <Loader2 size={18} className="animate-spin text-fieldiq-text" aria-label="Loading your drafts" />
      </div>
    );
  }
  const drafts = query.data ?? [];
  if (drafts.length === 0) return null;

  return (
    <section className="mt-5 w-full" aria-label="My drafts" data-testid="my-drafts">
      <h2 className="text-sm font-semibold text-white mb-2">My drafts ({drafts.length})</h2>
      <ul className="w-full rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
        {drafts.map(d => {
          const when = d.updated_at || d.created_at;
          return (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => navigate(`/forms/dynamic/${d.template_id}?draft=${d.id}`)}
                className="flex w-full min-h-[56px] items-center gap-3 px-4 py-3 text-left hover:bg-fieldiq-dark/50"
              >
                <FilePen size={20} className="shrink-0 text-fieldiq-text" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-white">{d.template_name || '—'}</span>
                  <span className="block text-xs text-fieldiq-muted">
                    Saved {when ? formatInstant(when, timeZone) : '—'}
                  </span>
                </span>
                <ChevronRight size={18} className="shrink-0 text-fieldiq-muted" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
